import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import {
  listHrExtraItems, createHrExtraItem, updateHrExtraItem, deleteHrExtraItem,
  listHrPeople, listHrContracts, copyHrAnnualExtrasYear,
} from '../../api/hr';
import { formatNumber } from '../../utils/format';
import EditableCell from './EditableCell';
import PersonTh from '../../components/PersonTh';
import PageHeader from '../../components/PageHeader';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import SegmentedToggle from '../../components/SegmentedToggle';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import { useTableSort } from '../../hooks/useTableSort';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { normalizeName, buildSearchKey, contractCoversToday } from '../../utils/personTable';
import './HrPage.css';

/* Compléments soumis aux charges sociales (hr_extra_items).
   Une ligne = un complément librement désigné par l'utilisateur (bonus, prime,
   jetons, …). `amount` = montant versé PAR MOIS coché ; total annuel = amount ×
   nombre de mois. Tous entrent dans l'assiette des charges sociales. */

const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const LABEL_SUGGESTIONS = ['Bonus', 'Prime', 'Jetons de présence', 'Gratification', 'Commission'];

// Options de la liste déroulante « Désignation » : les suggestions standard,
// + la valeur existante si elle sort de cette liste (compléments déjà saisis
// librement avant le passage en liste déroulante — on ne la perd pas).
function labelOptions(current) {
  const opts = LABEL_SUGGESTIONS.map(s => ({ value: s, label: p(s) }));
  if (current && !LABEL_SUGGESTIONS.includes(current)) {
    opts.unshift({ value: current, label: current });
  }
  return opts;
}

const COLUMNS = [
  { key: 'person',       label: p('Collaborateur'),    sortable: true, align: 'left',  personSort: true },
  { key: 'department',   label: tp('phrases:p_3d7c87c2af'),       sortable: true, align: 'left'  },
  { key: 'label',        label: tp('phrases:p_63a9062c64'),      sortable: true, align: 'left'  },
  { key: 'amount',       label: p('Montant / mois'),   sortable: true, align: 'right' },
  { key: 'months',       label: p('Mois de versement'), sortable: false, align: 'left'  },
  { key: 'annual_total', label: p('Total annuel'),     sortable: true, align: 'right',
    title: tp('phrases:p_5c23dff793') },
];

function monthsTotal(amount, months) {
  return Number(amount || 0) * (months?.length || 0);
}

export default function HrAnnualExtrasPage({ activeTenant, setPage, year: yearProp, onYearChange, embedded }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [localYear, setLocalYear] = useState(yearProp ?? new Date().getFullYear());
  const year = yearProp ?? localYear;
  const setYear = (y) => {
    const next = typeof y === 'function' ? y(year) : y;
    if (onYearChange) onYearChange(next);
    setLocalYear(next);
  };
  const [loading, setLoading] = useState(false);
  const sort = useTableSort(`hr_extras_sort_${tenantId}`, 'last_name_sort');
  const { sortCol, sortDir, handleSort: doSort, sortRows, nameOrder } = sort;
  const [search, setSearch] = useState('');
  const [status, setStatus] = usePersistentValue(`hr_extras_status_v2_${tenantId}`, 'all');
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [massOpen, setMassOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  function refresh() {
    if (!tenantId) return;
    setLoading(true);
    setSelected(new Set());
    Promise.all([listHrExtraItems(tenantId, year), listHrPeople(tenantId), listHrContracts(tenantId)])
      .then(([e, p, c]) => {
        if (!e.error) setItems(e.data || []);
        if (!p.error) setPeople(p.data || []);
        if (!c.error) setContracts(c.data || []);
      })
      .finally(() => setLoading(false));
  }
  useEffect(refresh, [tenantId, year]); // eslint-disable-line

  const personIndex = useMemo(() => {
    const m = {};
    for (const p of people) m[p.id] = p;
    return m;
  }, [people]);

  const deptByPerson = useMemo(() => {
    const open = {};
    for (const c of contracts) {
      if (c.end_date === null) {
        const cur = open[c.person_id];
        if (!cur || (c.start_date || '') > (cur.start_date || '')) open[c.person_id] = c;
      }
    }
    const latest = {};
    for (const c of contracts) {
      const cur = latest[c.person_id];
      if (!cur || (c.start_date || '') > (cur.start_date || '')) latest[c.person_id] = c;
    }
    const map = {};
    for (const pid of new Set([...Object.keys(open), ...Object.keys(latest)])) {
      const src = open[pid] || latest[pid];
      map[pid] = src?.department || '';
    }
    return map;
  }, [contracts]);

  // Personnes actives = au moins un contrat couvrant la date du jour.
  const activePersonIds = useMemo(() => {
    const s = new Set();
    for (const c of contracts) if (contractCoversToday(c)) s.add(c.person_id);
    return s;
  }, [contracts]);

  const enrichedRows = useMemo(() => items.map(r => {
    const p = personIndex[r.person_id];
    const dept = deptByPerson[r.person_id] || '';
    return {
      ...r,
      first_name_sort: p ? (p.first_name || '').toLowerCase() : '',
      last_name_sort:  p ? (p.last_name  || '').toLowerCase() : '',
      department: dept,
      _search: buildSearchKey(p?.first_name, p?.last_name, dept),
      annual_total: monthsTotal(r.amount, r.payment_months),
    };
  }), [items, personIndex, deptByPerson]);

  const filteredRows = useMemo(() => {
    let rows = enrichedRows;
    if (status === 'active') rows = rows.filter(r => activePersonIds.has(r.person_id));
    const q = normalizeName(search.trim());
    if (q) rows = rows.filter(r => r._search.includes(q));
    return rows;
  }, [enrichedRows, status, activePersonIds, search]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows),
    [filteredRows, sortCol, sortDir], // eslint-disable-line
  );

  const total = useMemo(
    () => filteredRows.reduce((acc, r) => acc + monthsTotal(r.amount, r.payment_months), 0),
    [filteredRows],
  );

  function handleSort(col) {
    const known = COLUMNS.find(c => c.key === col && c.sortable)
      || col === 'first_name_sort' || col === 'last_name_sort';
    if (!known) return;
    doSort(col);
  }

  async function patchItem(id, payload) {
    const r = await updateHrExtraItem(tenantId, id, payload);
    if (r.error) { setError(r.error); return; }
    setItems(prev => prev.map(x => x.id === id ? r.data : x));
  }

  const allSelected = sortedRows.length > 0 && sortedRows.every(r => selected.has(r.id));
  const someSelected = selected.size > 0 && !allSelected;

  function toggleRow(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(prev => (prev.size === sortedRows.length ? new Set() : new Set(sortedRows.map(r => r.id))));
  }
  function clearSelection() { setSelected(new Set()); }

  async function deleteSelected() {
    if (selected.size === 0 || submitting) return;
    if (!window.confirm(p('Supprimer {{count}} complément(s) ? Action irréversible.').replace('{{count}}', selected.size))) return;
    setSubmitting(true); setError(null);
    try {
      for (const id of selected) {
        const r = await deleteHrExtraItem(tenantId, id);
        if (r.error) { setError(r.error); break; }
        setItems(prev => prev.filter(x => x.id !== id));
      }
      clearSelection();
    } finally {
      setSubmitting(false);
    }
  }

  async function applyMassEdit(payload) {
    if (selected.size === 0) return false;
    setSubmitting(true); setError(null);
    try {
      for (const id of selected) {
        const r = await updateHrExtraItem(tenantId, id, payload);
        if (r.error) { setError(r.error); return false; }
        setItems(prev => prev.map(x => x.id === id ? r.data : x));
      }
      setMassOpen(false);
      clearSelection();
      return true;
    } finally {
      setSubmitting(false);
    }
  }

  async function addItem(payload) {
    const r = await createHrExtraItem(tenantId, payload);
    if (r.error) { setError(r.error); return false; }
    setItems(prev => [...prev, r.data]);
    setAddOpen(false);
    return true;
  }

  async function applyCopyYear(fromYear, toYear, overwrite) {
    setError(null);
    const r = await copyHrAnnualExtrasYear(tenantId, fromYear, toYear, overwrite);
    if (r.error) { setError(r.error); return false; }
    setCopyOpen(false);
    setYear(toYear);
    refresh();
    return true;
  }

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;

  /* Entête standard de carte (2026-09-01) : boutons en actions, filtres
     derrière l'entonnoir, recherche en dernier. */
  const nFilters = (status === 'active' ? 1 : 0) + (search.trim() ? 1 : 0);
  const stdFilters = (
    <>
      <FilterIcon />
      <SegmentedToggle
        compact
        label={p('Statut')} value={status} onChange={setStatus}
        options={[
          { value: 'all', label: p('Tous') },
          { value: 'active', label: p('Actifs'), tone: 'alert' },
        ]}
      />
      <FilterCount count={nFilters} onClear={() => { setStatus('all'); setSearch(''); }} />
    </>
  );
  const stdSearch = (
    <SearchField value={search} onChange={setSearch}
                 placeholder={t('phrases:p_224c3d8b39')} />
  );
  const controls = (
    <>
      <button className="hr-page__bulk-btn" onClick={() => { setError(null); setCopyOpen(true); }}
        title={t('phrases:p_a8766751c3')}>
        {p('Copier l\'année…')}
      </button>
      <button className="hr-page__add-btn" onClick={() => { setError(null); setAddOpen(true); }}>
        {p('+ Ajouter un complément')}
      </button>
      <button className="hr-page__bulk-btn" onClick={() => { setError(null); setMassOpen(true); }}
        disabled={selected.size === 0}
        title={selected.size === 0 ? p('Sélectionner d\'abord des lignes') : p('Édition groupée')}>
        Édition groupée{selected.size > 0 && ` (${selected.size})`}
      </button>
      <button className="hr-page__del-btn" onClick={deleteSelected}
        disabled={selected.size === 0 || submitting}
        title={t('phrases:p_387ba9aaf9')}>
        Supprimer{selected.size > 0 && ` (${selected.size})`}
      </button>
    </>
  );

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={t('phrases:p_dd46280847')}
          actions={setPage && <button className="hr-page__back" onClick={() => setPage('home')}>{p('← Accueil')}</button>}
        />
      )}

      {error && <div style={{ margin: '8px 0', color: 'var(--terracotta-dark)', fontSize: 13 }}>{error}</div>}
      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && (
        <CardShell
          title={<><span style={{ color: 'var(--color-amber)' }}>{t('phrases:p_dd46280847')}</span> <small>· {sortedRows.length} {p('ligne(s)')} · {year}
            {sortedRows.length !== items.length ? ` · ${p('filtrés sur')} ${items.length}` : ''}
            {selected.size > 0 ? ` · ${selected.size} ${p('sélectionné(s)')}` : ''}</small></>}
          primaryAction={controls}
          filters={stdFilters}
          search={stdSearch}
          filterCount={nFilters}
          collapsible storageKey="hr-extras"
          flush
        >
          {sortedRows.length === 0 ? (
            <div className="hr-page__empty">{p('Aucun complément pour ')}{year}{p('. Utiliser « + Ajouter un complément ».')}</div>
          ) : (
          <div className="hr-page__table-wrap">
            <table className="hr-page__table">
              <thead>
                <tr>
                  <th className="hr-page__check-col">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      title={allSelected ? p('Tout désélectionner') : p('Tout sélectionner')}
                    />
                  </th>
                  {COLUMNS.map(col => {
                    if (col.personSort) {
                      return <PersonTh key={col.key} sort={sort} className="hr-page__sort-th" style={{ textAlign: col.align }} />;
                    }
                    const isSorted = sortCol === col.key;
                    return (
                      <th key={col.key}
                        className={`hr-page__sort-th${isSorted ? ' is-sorted' : ''}${col.sortable ? '' : ' is-static'}`}
                        style={{ textAlign: col.align }}
                        onClick={() => col.sortable && handleSort(col.key)}
                        title={col.title || (col.sortable ? p('Cliquer pour trier') : '')}
                      >
                        <span>{col.label}</span>
                        {col.sortable && <span className="hr-page__sort-ind">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const p = personIndex[r.person_id];
                  const months = r.payment_months || [];
                  const isSel = selected.has(r.id);
                  return (
                    <tr key={r.id} className={isSel ? 'is-selected' : ''}>
                      <td className="hr-page__check-col">
                        <input type="checkbox" checked={isSel} onChange={() => toggleRow(r.id)} />
                      </td>
                      <td>{p ? <PersonName firstName={p.first_name} lastName={p.last_name} order={nameOrder} /> : <span className="hr-page__muted">{r.person_id.slice(0, 8)}…</span>}</td>
                      <td className={r.department ? '' : 'hr-page__muted'}>{r.department || '—'}</td>
                      <td>
                        <EditableCell value={r.label} align="left" type="select" nullable={false}
                          options={labelOptions(r.label)}
                          onCommit={v => { const s = (v || '').trim(); if (s) patchItem(r.id, { label: s }); }} />
                      </td>
                      <td className="hr-page__num">
                        <EditableCell value={r.amount} type="number" align="right"
                          onCommit={v => patchItem(r.id, { amount: v ?? 0 })} />
                      </td>
                      <td data-export-value={[...months].sort((a, b) => a - b).join(',')}>
                        <MonthPicker value={months}
                          onChange={ms => patchItem(r.id, { payment_months: ms })} />
                      </td>
                      <td className="hr-page__num"><strong>{formatNumber(r.annual_total, 0)}</strong></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="hr-page__total-row">
                  <td className="hr-page__check-col"></td>
                  <td><strong>{p('Total')}</strong></td>
                  <td></td><td></td><td></td><td></td>
                  <td className="hr-page__num"><strong>{formatNumber(total, 0)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}
        </CardShell>
      )}

      {addOpen && (
        <ExtraItemAddModal
          year={year}
          people={people}
          deptByPerson={deptByPerson}
          onCancel={() => { setAddOpen(false); setError(null); }}
          onAdd={addItem}
          error={error}
        />
      )}

      {copyOpen && (
        <ExtrasCopyYearModal
          currentYear={year}
          onCancel={() => { setCopyOpen(false); setError(null); }}
          onApply={applyCopyYear}
          error={error}
        />
      )}

      {massOpen && (
        <MassEditExtrasModal
          year={year}
          count={selected.size}
          onCancel={() => { setMassOpen(false); setError(null); }}
          onApply={applyMassEdit}
          error={error}
        />
      )}
    </main>
  );
}


function MassEditExtrasModal({ year, count, onCancel, onApply, error }) {
    const { t } = useTranslation();
  const p = useP();
const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [replaceMonths, setReplaceMonths] = useState(false);
  const [months, setMonths] = useState([12]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  async function apply() {
    const setLbl = label.trim() !== '';
    const setAmt = amount !== '';
    if (!setLbl && !setAmt && !replaceMonths) { setErr(p('Renseignez une désignation, un montant et/ou les mois.')); return; }
    if (replaceMonths && months.length === 0) { setErr(p('Sélectionnez au moins un mois.')); return; }
    setErr(null); setSubmitting(true);
    const payload = {};
    if (setLbl) payload.label = label.trim();
    if (setAmt) payload.amount = Number(amount);
    if (replaceMonths) payload.payment_months = [...months].sort((a, b) => a - b);
    const ok = await onApply(payload);
    if (!ok) setSubmitting(false);
  }

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal hr-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">Édition groupée — compléments {year}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <div className="hr-modal__field">
            <label className="hr-modal__label">{t('phrases:p_63a9062c64')}<span className="hr-modal__hint">{p('(vide = inchangé)')}</span></label>
            <select className="hr-modal__input" value={label} onChange={e => setLabel(e.target.value)}>
              <option value="">{p('— (inchangé)')}</option>
              {LABEL_SUGGESTIONS.map(s => <option key={s} value={s}>{p(s)}</option>)}
            </select>
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Montant par mois (CHF)')} <span className="hr-modal__hint">{p('(vide = inchangé)')}</span></label>
            <input className="hr-modal__input" type="number" step="1" min="0" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder={t('phrases:p_f94c4f7d80')} />
          </div>
          <div className="hr-modal__field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={replaceMonths} onChange={e => setReplaceMonths(e.target.checked)} />
              {p('Remplacer les mois de versement')}
            </label>
            {replaceMonths && <div style={{ marginTop: 8 }}><MonthPicker value={months} onChange={setMonths} /></div>}
          </div>
          <div className="hr-modal__field" style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
            {p('Applique les valeurs renseignées aux')} <strong>{count}</strong> {p('ligne(s) sélectionnée(s). Le total annuel est recalculé.')}
          </div>
          {(err || error) && <div className="hr-modal__error">{err || error}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary" onClick={apply} disabled={submitting}>
            {submitting ? p('Application…') : p('Appliquer')}
          </button>
        </div>
      </div>
    </div>
  );
}


function MonthPicker({ value, onChange }) {
  const { t } = useTranslation();
  const p = useP();
  const MONTHS_SHORT = React.useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  const set = new Set(value);
  const allOn = set.size === 12;
  return (
    <div className="hr-months">
      <button type="button"
        className={`hr-months__btn hr-months__btn--all${allOn ? ' is-on' : ''}`}
        onClick={() => onChange(allOn ? [12] : [...ALL_MONTHS])}
        title={allOn ? p('Tout décocher') : p('Tout cocher')}
      >{p('TOUS')}</button>
      {MONTHS_SHORT.map((m, i) => {
        const mn = i + 1;
        const on = set.has(mn);
        return (
          <button key={mn} type="button"
            className={`hr-months__btn${on ? ' is-on' : ''}`}
            onClick={() => {
              const next = new Set(set);
              if (next.has(mn)) { if (next.size === 1) return; next.delete(mn); }
              else next.add(mn);
              onChange([...next].sort((a, b) => a - b));
            }}
          >{m}</button>
        );
      })}
    </div>
  );
}


function ExtraItemAddModal({ year, people, deptByPerson, onCancel, onAdd, error }) {
  const { t } = useTranslation();
  const p = useP();
  const [personId, setPersonId] = useState('');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [months, setMonths] = useState([12]);
  const [submitting, setSubmitting] = useState(false);

  const sortedPeople = useMemo(
    () => [...people].sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '')),
    [people],
  );

  const valid = personId && label.trim() && months.length > 0 && Number(amount) >= 0 && amount !== '';

  async function submit() {
    if (!valid || submitting) return;
    setSubmitting(true);
    const ok = await onAdd({
      person_id: personId,
      year: Number(year),
      label: label.trim(),
      amount: Number(amount),
      payment_months: months,
    });
    if (!ok) setSubmitting(false);
  }

  const annual = (Number(amount) || 0) * months.length;

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal hr-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{p('Ajouter un complément — ')}{year}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Collaborateur')}</label>
            <select className="hr-modal__input" value={personId} onChange={e => setPersonId(e.target.value)}>
              <option value="">— Choisir —</option>
              {sortedPeople.map(person => (
                <option key={person.id} value={person.id}>
                  {(person.last_name || '').toUpperCase()} {person.first_name}{deptByPerson[person.id] ? ` · ${deptByPerson[person.id]}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{t('phrases:p_63a9062c64')}</label>
            <select className="hr-modal__input" value={label} onChange={e => setLabel(e.target.value)}>
              <option value="">— choisir —</option>
              {LABEL_SUGGESTIONS.map(s => <option key={s} value={s}>{p(s)}</option>)}
            </select>
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Montant par mois (CHF)')}</label>
            <input className="hr-modal__input" type="number" step="1" min="0" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="0" />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Mois de versement')} <span className="hr-modal__hint">(au moins un)</span></label>
            <MonthPicker value={months} onChange={setMonths} />
          </div>
          <div className="hr-modal__field" style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
            {p('Total annuel :')} <strong>{formatNumber(annual, 0)} CHF</strong> ({months.length} mois × {formatNumber(Number(amount) || 0, 0)})
          </div>
          {error && <div className="hr-modal__error">{error}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary" onClick={submit} disabled={!valid || submitting}>
            {submitting ? p('Ajout…') : t('common:actions.add')}
          </button>
        </div>
      </div>
    </div>
  );
}


function ExtrasCopyYearModal({ currentYear, onCancel, onApply, error }) {
  const { t } = useTranslation();
  const p = useP();
  const [fromYear, setFromYear] = useState(currentYear);
  const [toYear, setToYear]     = useState(currentYear + 1);
  const [overwrite, setOverwrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sameYear = fromYear === toYear;

  async function submit() {
    if (sameYear || submitting) return;
    setSubmitting(true);
    try { await onApply(fromYear, toYear, overwrite); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{p('Copier les compléments — année à année')}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Année source')}</label>
            <input className="hr-modal__input" type="number" min="2000" max="2100"
                   value={fromYear} onChange={e => setFromYear(Number(e.target.value))} />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Année cible')}</label>
            <input className="hr-modal__input" type="number" min="2000" max="2100"
                   value={toYear} onChange={e => setToYear(Number(e.target.value))} />
          </div>
          <div className="hr-modal__field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
              Remplacer les compléments existants de {toYear} (sinon : ajoutés)
            </label>
          </div>
          {sameYear && <div className="hr-modal__error">{p('Les années source et cible doivent différer.')}</div>}
          {error && <div className="hr-modal__error">{error}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary" onClick={submit} disabled={sameYear || submitting}>
            {submitting ? p('Copie…') : `Copier ${fromYear} → ${toYear}`}
          </button>
        </div>
      </div>
    </div>
  );
}
